import { createContext, useContext } from "react";
import type { ReactNode } from "react";

type MountFn = (id: string, element: ReactNode) => void;
type UnmountFn = (id: string) => void;
type RegisterFn = (id: string) => void;
type UnregisterFn = (id: string) => void;
type SetBodyLockFn = (id: string, locking: boolean) => void;

/**
 * Stable surface of DialogHost: methods plus the `mounted` flag. Identity
 * never changes after the host mounts, so consumers depending on this
 * context don't re-render when dialogs open or close. Anything that needs
 * to react to the open-stack changing should consume DialogHostStackContext.
 */
export interface DialogHostMutations {
  mount: MountFn;
  unmount: UnmountFn;
  /** Push a dialog onto the open stack. Position in stack drives z-index. */
  register: RegisterFn;
  /** Remove a dialog from the open stack. */
  unregister: UnregisterFn;
  /** Toggle this dialog's contribution to the body scroll lock counter. */
  setBodyLock: SetBodyLockFn;
  /**
   * True only when a real DialogHost is mounted above. Tests and stories
   * without DialogHost get the default value with `mounted: false`, which
   * lets Dialog fall back to rendering its content inline so assertions
   * still find the DOM.
   */
  mounted: boolean;
}

const noop = () => {};

const defaultMutations: DialogHostMutations = {
  mount: noop,
  unmount: noop,
  register: noop,
  unregister: noop,
  setBodyLock: noop,
  mounted: false,
};

export const DialogHostMutationContext =
  createContext<DialogHostMutations>(defaultMutations);

/**
 * Currently-open dialog ids in open order. Index = depth. Separated from
 * mutations so re-rendering on stack change doesn't ripple into consumers
 * that only need the (stable) mutation methods.
 */
export const DialogHostStackContext = createContext<readonly string[]>([]);

export const useDialogHost = () => useContext(DialogHostMutationContext);

/**
 * Returns the dialog's position in the global open stack (0 = bottom).
 * Falls back to 0 when not yet registered or no DialogHost is mounted.
 */
export function useDialogStackPosition(id: string): number {
  const stack = useContext(DialogHostStackContext);
  const index = stack.indexOf(id);
  return index < 0 ? 0 : index;
}
